import { useState, useEffect, useMemo, useRef } from "react";
import { getConstellation, getContributions } from "./supabaseClient";
import { useTheme, PageWrap, ThemeToggle, Starfield, SERIF, SANS, PLAY, injectFonts } from "./theme";

// ═══════════════════════════════════════════
// INTERACTIVE CONSTELLATION EXPERIENCE
// Entrance: one line alone → then the constellation resolves around it
// Then: tap a star → see the quote
// ═══════════════════════════════════════════

// Layout engine — position stars in a constellation
function layoutStars(count, cx, cy, radius) {
  var positions = [];
  for (var i = 0; i < count; i++) {
    var angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    var r = radius * (0.85 + Math.random() * 0.3);
    positions.push({
      x: cx + Math.cos(angle) * r,
      y: cy + Math.sin(angle) * r,
    });
  }
  return positions;
}

// entrance timing (ms)
var T_IN = 300;     // quote begins fading up
var T_HOLD = 2900;  // quote starts leaving
var T_DONE = 4000;  // overlay gone, card fully present

export default function Experience() {
  var { mode, t, toggle } = useTheme();
  var [constellation, setConstellation] = useState(null);
  var [contributions, setContributions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [activeStar, setActiveStar] = useState(null);
  var [visited, setVisited] = useState({});
  var [error, setError] = useState(null);

  // entrance: "wait" | "in" | "leaving" | "done"
  var [phase, setPhase] = useState("wait");
  var timers = useRef([]);

  var params = new URLSearchParams(window.location.search);
  var slug = params.get("c");
  var viewer = params.get("v");   // optional: which contributor is looking

  useEffect(function() {
    injectFonts();
    if (!slug) { setError("No constellation link."); setLoading(false); return; }
    getConstellation(slug).then(function(c) {
      if (!c) { setError("Constellation not found."); setLoading(false); return; }
      setConstellation(c);
      getContributions(c.id).then(function(ct) {
        setContributions(ct || []);
        setLoading(false);
      });
    });
  }, [slug]);

  // Build star data from contributions
  var starData = useMemo(function() {
    if (!contributions.length) return [];
    return contributions.map(function(c, i) {
      var userMsgs = (c.transcript || []).filter(function(m) {
        return m.role === "user" && m.content.indexOf("Inside joke:") !== 0;
      });
      var quote = userMsgs.map(function(m) { return m.content; }).join(" ");
      if (quote.length > 200) quote = quote.substring(0, 200) + "…";

      var adjectives = (c.elements || []).map(function(a) {
        return a.charAt(0).toUpperCase() + a.slice(1);
      });

      var insideJoke = (c.transcript || []).filter(function(m) {
        return m.role === "user" && m.content.indexOf("Inside joke:") === 0;
      }).map(function(m) { return m.content.replace("Inside joke: ", ""); }).join(" ");

      return {
        id: i,
        contributor: c.contributor_name || "Anonymous",
        relationship: c.relationship || "",
        quote: quote,
        adjectives: adjectives,
        insideJoke: insideJoke,
        label: c.star_label || adjectives[0] || "",
      };
    });
  }, [contributions]);

  // Which line opens the piece.
  // If the link carries ?v= we lead with THEIR line — personal, then collective.
  // Otherwise the shortest real quote wins: only a short line can be set huge.
  var featured = useMemo(function() {
    if (!starData.length) return -1;
    if (viewer) {
      var want = String(viewer).trim().toLowerCase();
      for (var i = 0; i < starData.length; i++) {
        if (String(i) === want) return i;
        if (starData[i].contributor.toLowerCase() === want) return i;
      }
    }
    var best = -1, bestLen = Infinity;
    starData.forEach(function(s, j) {
      var len = (s.quote || "").trim().length;
      if (len > 12 && len < bestLen) { bestLen = len; best = j; }
    });
    return best > -1 ? best : 0;
  }, [starData, viewer]);

  // Position stars
  var positions = useMemo(function() {
    return layoutStars(starData.length, 50, 45, 28);
  }, [starData.length]);

  // ── run the entrance once the data is in ──
  useEffect(function() {
    if (loading || error || featured < 0) return;

    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setPhase("done"); return; }

    timers.current.push(setTimeout(function(){ setPhase("in"); }, T_IN));
    timers.current.push(setTimeout(function(){ setPhase("leaving"); }, T_HOLD));
    timers.current.push(setTimeout(function(){
      setPhase("done");
      setVisited(function(v){ var n = Object.assign({}, v); n[featured] = true; return n; });
    }, T_DONE));

    return function() {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [loading, error, featured]);

  function skipEntrance() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase("done");
    if (featured > -1) {
      setVisited(function(v){ var n = Object.assign({}, v); n[featured] = true; return n; });
    }
  }

  var handleStarClick = function(index) {
    if (activeStar === index) {
      setActiveStar(null);
    } else {
      setActiveStar(index);
      var v = Object.assign({}, visited);
      v[index] = true;
      setVisited(v);
    }
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#050510", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "rgba(255,255,255,0.3)", fontFamily: SANS, fontSize: 13, letterSpacing: 2 }}>Loading…</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "100vh", background: "#050510", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#C87070", fontFamily: SANS, fontSize: 14 }}>{error}</p>
    </div>
  );

  var recipientName = constellation ? constellation.recipient_name : "";
  var visitedCount = Object.keys(visited).length;
  var totalStars = starData.length;
  var entering = phase !== "done";

  // Theme-aware colors
  var cardBg = mode === "dark"
    ? "linear-gradient(135deg, #050510, #0A0A18, #0F0F28)"
    : "linear-gradient(135deg, #F0EDF5, #E8E4EE, #F5F2F8)";
  var cardBorder = mode === "dark" ? "rgba(192,192,192,0.06)" : "rgba(60,40,80,0.1)";
  var cardShadow = mode === "dark"
    ? "0 24px 80px rgba(0,0,0,0.6), inset 0 0 80px rgba(80,60,140,0.03)"
    : "0 24px 80px rgba(0,0,0,0.08), inset 0 0 80px rgba(80,60,140,0.02)";
  var starColor = mode === "dark" ? "rgba(192,192,192,0.5)" : "rgba(80,60,120,0.5)";
  var starVisited = mode === "dark" ? "rgba(184,150,122,0.8)" : "rgba(139,111,88,0.8)";
  var labelColor = mode === "dark" ? "rgba(192,192,192,0.55)" : "rgba(60,40,80,0.55)";
  var labelVisited = mode === "dark" ? "rgba(184,150,122,0.75)" : "rgba(139,111,88,0.75)";
  var lineColor = mode === "dark" ? "rgba(192,192,192,0.12)" : "rgba(80,60,120,0.12)";
  var overlayBg = mode === "dark" ? "rgba(5,5,16,0.88)" : "rgba(240,237,245,0.94)";
  var quoteColor = mode === "dark" ? "#FFFFFF" : "#1A1720";
  var pillBorder = mode === "dark" ? "rgba(192,192,192,0.15)" : "rgba(60,40,80,0.15)";
  var pillText = mode === "dark" ? "rgba(192,192,192,0.62)" : "rgba(60,40,80,0.62)";
  var curtainBg = mode === "dark" ? "#050510" : "#F4F1F8";

  // where the featured star sits, so the line retreats toward it
  var fpos = featured > -1 ? positions[featured] : null;
  var dx = fpos ? (fpos.x - 50) * 0.5 : 0;
  var dy = fpos ? (fpos.y - 45) * 0.4 : 0;

  return (
    <PageWrap t={t}>
      <style>{`
        @keyframes xpPulse { 0%, 100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.3); opacity: 1; } }
        @keyframes xpFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes xpFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }

        /* ── the entrance ── */
        .xp-curtain {
          position: fixed; inset: 0; z-index: 60;
          display: flex; align-items: center; justify-content: center;
          padding: 40px; text-align: center;
          transition: opacity 1.05s ease, visibility 1.05s;
        }
        .xp-curtain.is-gone { opacity: 0; visibility: hidden; pointer-events: none; }
        .xp-line {
          opacity: 0; transform: translateY(16px) scale(1);
          transition: opacity 1.15s ease, transform 1.15s cubic-bezier(.22,.7,.28,1);
          max-width: 620px;
        }
        .xp-curtain.is-in .xp-line { opacity: 1; transform: none; }
        .xp-curtain.is-leaving .xp-line {
          opacity: 0;
          transform: translate(var(--dx), var(--dy)) scale(.32);
          transition: opacity .9s ease, transform 1.05s cubic-bezier(.5,0,.75,0);
        }
        .xp-attr {
          opacity: 0; transition: opacity .9s ease .5s;
        }
        .xp-curtain.is-in .xp-attr { opacity: 1; }
        .xp-skip {
          position: absolute; bottom: 38px; left: 50%; transform: translateX(-50%);
          background: none; border: 0; cursor: pointer; font-family: inherit;
          opacity: 0; transition: opacity .8s ease 1.4s;
        }
        .xp-curtain.is-in .xp-skip { opacity: 1; }
        .xp-stage { transition: opacity 1.1s ease, transform 1.1s cubic-bezier(.2,.8,.3,1); }
        .xp-stage.is-hidden { opacity: 0; transform: scale(.985); }
      `}</style>

      {/* ── THE ENTRANCE — one line, alone ── */}
      {featured > -1 && starData[featured] && (
        <div
          className={"xp-curtain" + (phase === "in" ? " is-in" : "") + (phase === "leaving" ? " is-in is-leaving" : "") + (phase === "done" ? " is-gone" : "")}
          style={{ background: curtainBg, "--dx": dx + "vw", "--dy": dy + "vh" }}
          onClick={skipEntrance}
        >
          <div className="xp-line">
            <p style={{
              fontFamily: SERIF, fontStyle: "italic",
              fontSize: "clamp(24px, 5.2vw, 40px)", fontWeight: 300,
              color: quoteColor, lineHeight: 1.55, margin: 0, letterSpacing: -0.2,
            }}>"{starData[featured].quote}"</p>
            <p className="xp-attr" style={{
              fontFamily: SANS, fontSize: 11, fontWeight: 400,
              letterSpacing: 5, color: t.accentSoft,
              textTransform: "uppercase", margin: "34px 0 0",
            }}>{starData[featured].contributor}</p>
          </div>
          <button className="xp-skip" type="button" onClick={skipEntrance} style={{
            fontFamily: SANS, fontSize: 10, fontWeight: 400, letterSpacing: 4,
            color: t.textMuted, textTransform: "uppercase",
          }}>Skip</button>
        </div>
      )}

      <div className={"xp-stage" + (entering ? " is-hidden" : "")}>
        <ThemeToggle t={t} toggle={toggle} mode={mode} />

        {/* ── HEADER — branding + tagline above card ── */}
        <div style={{
          textAlign: "center", padding: "40px 24px 24px",
          position: "relative", zIndex: 2,
        }}>
          <p style={{
            fontFamily: SANS, fontSize: 11, fontWeight: 500, letterSpacing: 5,
            color: t.accentSoft, textTransform: "uppercase", margin: "0 0 20px",
          }}>Yours Truly</p>
          <p style={{
            fontFamily: SERIF, fontStyle: "italic", fontSize: "clamp(15px, 2.5vw, 18px)",
            fontWeight: 400, color: t.textSoft, margin: 0, lineHeight: 1.8,
          }}>Every star is a person. Tap to see what they said.</p>
        </div>

        {/* ── CONSTELLATION CARD — Starlight aesthetic ── */}
        <div style={{
          maxWidth: 420, width: "calc(100% - 48px)", margin: "0 auto",
          background: cardBg,
          border: "1px solid " + cardBorder,
          borderRadius: 6, position: "relative",
          aspectRatio: "3 / 4", overflow: "hidden",
          boxShadow: cardShadow,
        }}>
          {/* Card inner glow */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
            background: "radial-gradient(ellipse at 50% 30%, rgba(120,100,180,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }} />

          <div style={{ height: "8%" }} />

          {/* Stars inside card */}
          <div style={{
            position: "relative", width: "100%", height: "65%",
            zIndex: 1,
          }}>
            {/* Lines between stars */}
            <svg style={{
              position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
              pointerEvents: "none",
            }} viewBox="0 0 100 90">
              {positions.map(function(p, i) {
                var next = positions[(i + 1) % positions.length];
                return (
                  <line key={"line" + i}
                    x1={p.x} y1={p.y} x2={next.x} y2={next.y}
                    stroke={lineColor} strokeWidth="0.15"
                  />
                );
              })}
            </svg>

            {/* Star dots */}
            {starData.map(function(star, i) {
              var pos = positions[i];
              if (!pos) return null;
              var isActive = activeStar === i;
              var isVisited = visited[i];

              return (
                <div key={"star" + i}
                  onClick={function() { handleStarClick(i); }}
                  style={{
                    position: "absolute",
                    left: pos.x + "%", top: pos.y + "%",
                    transform: "translate(-50%, -50%)",
                    cursor: "pointer", zIndex: isActive ? 10 : 2,
                    transition: "all 0.4s ease",
                  }}
                >
                  <div style={{
                    fontSize: isActive ? 18 : 12,
                    color: isVisited ? starVisited : starColor,
                    animation: isActive ? "none" : "xpPulse 3s ease-in-out " + (i * 0.5) + "s infinite",
                    transition: "all 0.4s ease",
                    textAlign: "center",
                    lineHeight: 1,
                  }}>✧</div>
                  <p style={{
                    fontFamily: SANS, fontSize: 9, fontWeight: 400,
                    color: isVisited ? labelVisited : labelColor,
                    textAlign: "center", marginTop: 4,
                    letterSpacing: 1, textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}>{star.contributor}</p>
                </div>
              );
            })}
          </div>

          {/* Progress inside card */}
          {visitedCount > 0 && (
            <div style={{ textAlign: "center", position: "absolute", bottom: "5%", width: "100%", zIndex: 2 }}>
              <p style={{
                fontFamily: SANS, fontSize: 10, fontWeight: 400,
                color: mode === "dark" ? "rgba(192,192,192,0.45)" : "rgba(60,40,80,0.45)",
                letterSpacing: 2,
              }}>{visitedCount} of {totalStars} explored</p>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div style={{
          textAlign: "center", padding: "24px 24px 48px",
          position: "relative", zIndex: 2,
        }}>
          {visitedCount === totalStars && totalStars > 0 && (
            <div style={{ animation: "xpFadeIn 0.8s ease-out", marginBottom: 24 }}>
              <p style={{
                fontFamily: SERIF, fontStyle: "italic", fontSize: 17, fontWeight: 400,
                color: t.textSoft, marginBottom: 20,
              }}>You've explored every star.</p>
              <a href={"/constellation?c=" + slug} style={{
                display: "inline-block", padding: "16px 38px",
                border: "1px solid " + t.border,
                fontFamily: SANS, fontSize: 12, fontWeight: 500,
                letterSpacing: 2.4, color: t.textSoft,
                textDecoration: "none", textTransform: "uppercase",
                transition: "all 0.3s",
              }}>Order a Framed Poster</a>
            </div>
          )}
          <p style={{
            fontFamily: SANS, fontSize: 10, fontWeight: 500,
            letterSpacing: 5, color: t.textMuted,
            textTransform: "uppercase",
          }}>Yours Truly</p>
        </div>
      </div>

      {/* ── QUOTE OVERLAY — appears when a star is tapped ── */}
      {activeStar !== null && starData[activeStar] && (
        <div
          onClick={function() { setActiveStar(null); }}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: overlayBg, zIndex: 20,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 32, cursor: "pointer",
          }}
        >
          <div
            onClick={function(e) { e.stopPropagation(); }}
            style={{
              maxWidth: 420, width: "100%", textAlign: "center",
              animation: "xpFadeIn 0.4s ease-out",
              cursor: "default",
            }}
          >
            <div style={{
              fontSize: 32, marginBottom: 24, color: t.accent,
              animation: "xpFloat 3s ease-in-out infinite",
            }}>✦</div>

            <p style={{
              fontFamily: SANS, fontSize: 11, fontWeight: 500,
              letterSpacing: 5, color: t.accentSoft,
              textTransform: "uppercase", marginBottom: 24,
            }}>{starData[activeStar].contributor}</p>

            {starData[activeStar].relationship && (
              <p style={{
                fontFamily: SANS, fontSize: 12, fontWeight: 400,
                color: t.textMuted, marginBottom: 32,
                textTransform: "capitalize",
              }}>{starData[activeStar].relationship}</p>
            )}

            <p style={{
              fontFamily: SERIF, fontStyle: "italic",
              fontSize: "clamp(19px, 4vw, 25px)", fontWeight: 400,
              color: quoteColor, lineHeight: 1.8,
              maxWidth: 380, margin: "0 auto 32px",
            }}>"{starData[activeStar].quote}"</p>

            {starData[activeStar].adjectives.length > 0 && (
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 8,
                justifyContent: "center", marginBottom: 24,
              }}>
                {starData[activeStar].adjectives.map(function(adj, ai) {
                  return (
                    <span key={ai} style={{
                      padding: "7px 15px", borderRadius: 16,
                      border: "1px solid " + pillBorder,
                      fontFamily: SANS, fontSize: 12, fontWeight: 400,
                      color: pillText, letterSpacing: .6,
                    }}>{adj}</span>
                  );
                })}
              </div>
            )}

            {starData[activeStar].insideJoke && (
              <p style={{
                fontFamily: SERIF, fontSize: 15, fontWeight: 400,
                color: t.accentSoft, fontStyle: "italic",
                marginTop: 16,
              }}>{starData[activeStar].insideJoke}</p>
            )}

            <p style={{
              fontFamily: SANS, fontSize: 10, fontWeight: 500,
              color: t.textMuted, marginTop: 40,
              letterSpacing: 3, textTransform: "uppercase",
            }}>Tap anywhere to close</p>
          </div>
        </div>
      )}
    </PageWrap>
  );
}
